import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react/conditional-hooks-factory';

import { expValEquals } from '@confluence/feature-experiments';

import { LegacyMacroRendererContext } from './LegacyMacroRendererContext.tsx';
import { UnknownMacroPlaceholder } from './UnknownMacroPlaceholder.tsx';

const AC_NS = 'ac-xml-namespace';

/**
 * Parses macroBodyStorage XML and returns the document.
 * Wraps in a root element with namespace declarations so ac:/ri: prefixed tags are valid XML.
 */
function parseStorageXml(storageXml: string): Document | null {
	if (!storageXml) return null;
	try {
		const wrapped = `<root xmlns:ac="${AC_NS}" xmlns:ri="${AC_NS}">${storageXml}</root>`;
		const doc = new DOMParser().parseFromString(wrapped, 'text/xml');
		// DOMParser does not throw on parse errors — it returns a document containing
		// a <parsererror> element. Detect and discard these silently.
		if (doc.querySelector('parsererror')) return null;
		return doc;
	} catch {
		return null;
	}
}

/**
 * Recursively renders a storage XML node as React nodes.
 * - <ac:structured-macro> → <UnknownMacroPlaceholder> with recursive children
 * - <ac:parameter> → skipped (metadata, not body content)
 * - <ac:rich-text-body> / <ac:plain-text-body> → recurse into children
 * - Text/CDATA nodes → plain text string
 * - Everything else → recurse into children
 */
function renderStorageNode(node: Node, key: number): React.ReactNode {
	// TEXT_NODE (3) and CDATA_SECTION_NODE (4) both contain plain text content
	if (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.CDATA_SECTION_NODE) {
		return node.textContent?.trim() || null;
	}
	if (node.nodeType !== Node.ELEMENT_NODE) return null;

	const el = node as Element;

	// Skip parameter nodes — metadata only, not body content
	if (el.localName === 'parameter') return null;

	// Structured macro → render as a nested UnknownMacroPlaceholder
	if (el.localName === 'structured-macro') {
		const macroName = el.getAttributeNS(AC_NS, 'name') ?? 'unknown';
		const children = renderStorageChildren(el);
		return (
			<UnknownMacroPlaceholder key={key} macroTitle={macroName}>
				{children}
			</UnknownMacroPlaceholder>
		);
	}

	// For all other elements (rich-text-body, plain-text-body, p, etc.) recurse into children
	return <React.Fragment key={key}>{renderStorageChildren(el)}</React.Fragment>;
}

function renderStorageChildren(node: Node): React.ReactNode {
	const children: React.ReactNode[] = [];
	node.childNodes.forEach((child, i) => {
		const rendered = renderStorageNode(child, i);
		if (rendered !== null) children.push(rendered);
	});
	return children.length ? children : null;
}

/**
 * Builds React children for the Nth top-level <ac:structured-macro> in the storage document.
 * macroBodyStorage is the body content of the outer macro — each top-level structured-macro
 * in it corresponds to one wysiwyg-unknown-macro img in the rendered HTML, in document order.
 * If there are no top-level structured-macros (plain body content), render everything directly.
 */
function buildBodyForImg(doc: Document, imgIndex: number): React.ReactNode {
	const topLevelMacros: Element[] = [];
	doc.documentElement.childNodes.forEach((child) => {
		if (
			child.nodeType === Node.ELEMENT_NODE &&
			(child as Element).localName === 'structured-macro'
		) {
			topLevelMacros.push(child as Element);
		}
	});

	if (topLevelMacros.length > 0) {
		const macro = topLevelMacros[imgIndex];
		if (!macro) return null;
		return renderStorageChildren(macro);
	}

	// No top-level structured-macros — storage is plain body content, render directly.
	// Only for the first img (index 0) since there's only one body.
	if (imgIndex === 0) {
		return renderStorageChildren(doc.documentElement);
	}

	return null;
}

/**
 * Extracts the macro name from a wysiwyg-unknown-macro img src URL.
 * The src contains a `name=` query parameter with the macro name.
 * Returns null if not found
 */
function getMacroNameFromImg(img: HTMLImageElement): string | null {
	try {
		const url = new URL(img.src, window.location.origin);
		return url.searchParams.get('name');
	} catch {
		return null;
	}
}

type PortalTarget = { el: HTMLElement; title: string; body: React.ReactNode };

/**
 * Finds all wysiwyg-unknown-macro img elements inside the given container,
 * replaces each with a div portal target, and returns the portals to render.
 *
 * Call this hook when the macro is an unknown macro (appKey === 'unknown').
 * Pass the returned portals JSX as a sibling to the macro's rendered output.
 */
export function useUnknownMacroPortal({
	container,
	macroBodyStorage,
}: {
	container: Element | null;
	macroBodyStorage: string | null;
}): React.ReactNode {
	const { shouldRenderUnknownMacro } = useContext(LegacyMacroRendererContext);

	const [portalTargets, setPortalTargets] = useState<PortalTarget[]>([]);

	useEffect(() => {
		if (!shouldRenderUnknownMacro || !container || !macroBodyStorage) return;

		const imgs = container.querySelectorAll<HTMLImageElement>('img.wysiwyg-unknown-macro');
		if (!imgs.length) return;

		const doc = parseStorageXml(macroBodyStorage);
		if (!doc) return;

		const targets: PortalTarget[] = [];
		imgs.forEach((img, imgIndex) => {
			const title = getMacroNameFromImg(img);
			if (!title) return;

			const body = buildBodyForImg(doc, imgIndex);
			if (body === null) return;

			// Replace each img with a div that becomes the portal target.
			// This preserves the surrounding HTML tree structure completely.
			const placeholder = document.createElement('div');
			img.replaceWith(placeholder);
			targets.push({ el: placeholder, title, body });
		});
		setPortalTargets(targets);

		return () => {
			setPortalTargets([]);
		};
	}, [container, macroBodyStorage, shouldRenderUnknownMacro]);

	if (!portalTargets.length) return null;

	return (
		<>
			{portalTargets.map(({ el, title, body }, i) =>
				createPortal(
					<UnknownMacroPlaceholder macroTitle={title}>{body}</UnknownMacroPlaceholder>,
					el,
					String(i),
				),
			)}
		</>
	);
}

export const useUnknownMacroPortalConditional: (args_0: {
	container: Element | null;
	macroBodyStorage: string | null;
}) =>
	| string
	| number
	| boolean
	| React.ReactElement<any, string | React.JSXElementConstructor<any>>
	| Iterable<React.ReactNode>
	| React.ReactPortal
	| null
	| undefined = conditionalHooksFactory(
	() => expValEquals('platform_editor_unknown_macro_lcm_view_mode', 'isEnabled', true),
	useUnknownMacroPortal,
	() => null,
);
